import { computed, onMounted, onUnmounted, shallowRef } from 'vue'
import dayjs, { type Dayjs } from 'dayjs'
import { useAuthStore } from '@/stores/authStore'

// RD-160 / D-001: календарное время AppCalendar — department.timezone принятого профиля
// (GET /api/user/me/ → body.department.timezone), по аналогии с main-mobile/RD-123.
// Фиксированное смещение UTC, не IANA-зона. Нет/невалидно — время устройства.

const MIN_OFFSET = -12 * 60
const MAX_OFFSET = 14 * 60
const DECIMAL_HOURS = /^([+-])?(\d{1,2})(?:\.(\d{1,9}))?$/
const HOURS_MINUTES = /^([+-])(\d{1,2}):(\d{2})$/

/** Смещение в целых минутах; null — значение отсутствует/невалидно (используется устройство). */
export function parseProfileOffset(value: unknown): number | null {
    if (typeof value !== 'string') return null
    const text = value.trim()
    let minutes: number
    let match = HOURS_MINUTES.exec(text)
    if (match) {
        if (Number(match[3]) > 59) return null
        minutes = (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3]))
    } else {
        match = DECIMAL_HOURS.exec(text)
        if (!match) return null
        const fraction = match[3] ?? ''
        const scale = 10 ** fraction.length
        // 5.5 → 330, 5.75 → 345; дробь, не дающая целых минут (5.01), отклоняется, а не округляется.
        const fractionMinutes = Number(fraction || '0') * 60
        if (fractionMinutes % scale !== 0) return null
        minutes = (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 60 + fractionMinutes / scale)
    }
    if (minutes < MIN_OFFSET || minutes > MAX_OFFSET) return null
    return minutes === 0 ? 0 : minutes
}

const pad = (value: number) => String(value).padStart(2, '0')

/** Календарный день момента: в смещении профиля либо (null) в поясе устройства. 'YYYY-MM-DD'. */
export function calendarDayKey(time: number, offset: number | null): string {
    if (offset === null) return dayjs(time).format('YYYY-MM-DD')
    const date = new Date(time + offset * 60_000)
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
}

// Общие часы календарей: тик на границе минуты (покрывает полночь в любом смещении),
// при возврате из фона и pageshow. Работают, пока смонтирован хотя бы один календарь.
const clock = shallowRef(Date.now())
let subscribers = 0
let timer: ReturnType<typeof setTimeout> | undefined

/** Обновить «сейчас» немедленно (например, перед применением preset). */
export function refreshCalendarClock() {
    if (timer !== undefined) clearTimeout(timer)
    clock.value = Date.now()
    timer = subscribers > 0 ? setTimeout(refreshCalendarClock, 60_000 - (clock.value % 60_000) + 50) : undefined
}

function onVisibilityChange() {
    if (document.visibilityState === 'visible') refreshCalendarClock()
}

/**
 * «Сегодня» календаря: Dayjs начала календарного дня (локальная полночь той же даты,
 * как и даты ячеек). Пересчитывается при смене дня, возврате из фона и смене/сбросе timezone профиля.
 * Выбранные даты не трогает — это только источник маркера «сегодня» и границ preset.
 */
export function useCalendarToday() {
    const authStore = useAuthStore()
    // Первый рендер до onMounted не должен видеть «сейчас» времени загрузки модуля.
    clock.value = Date.now()
    const offset = computed(() => parseProfileOffset(authStore.userInfo?.department?.timezone))
    const todayKey = computed(() => calendarDayKey(clock.value, offset.value))

    onMounted(() => {
        if (subscribers++ === 0) {
            document.addEventListener('visibilitychange', onVisibilityChange)
            window.addEventListener('pageshow', refreshCalendarClock)
        }
        refreshCalendarClock()
    })
    onUnmounted(() => {
        if (--subscribers > 0) return
        document.removeEventListener('visibilitychange', onVisibilityChange)
        window.removeEventListener('pageshow', refreshCalendarClock)
        if (timer !== undefined) clearTimeout(timer)
        timer = undefined
    })

    return computed<Dayjs>(() => dayjs(todayKey.value))
}
